'use client'

import { useSearchParams } from 'next/navigation'
import React, { useEffect, useState } from 'react'

import { IPromotion } from '../../types/schema'

import '@/styles/components/_template_Booking_Online.scss'

import { PromotionButtonBooking } from '@/components/promotion/promotionButtonBooking'
import { useChangeBaseColor } from '@/hooks/useChangeBaseColor'
import UtmHandler from '@/libs/UtmHandler'

interface BookingOnlineProps {
  promotionData: IPromotion
}

const Temlate_Booking_Online: React.FC<BookingOnlineProps> = ({ promotionData }) => {
  const searchParams = useSearchParams()
  const [isVisible, setIsVisible] = useState(false)
  useChangeBaseColor('bg-theme-100')

  useEffect(() => {
    setIsVisible(true)
  }, [])

  // Helper function to check if promotion has specific available dates
  const hasSpecificDates = promotionData.listtime && Array.isArray(promotionData.listtime)

  // Helper function to get listtime as array
  const getListtimeArray = () => {
    if (!promotionData.listtime) return []
    return Array.isArray(promotionData.listtime) ? promotionData.listtime : [promotionData.listtime]
  }

  // Calculate expiration based on listtime array or fallback to single date
  const calculateExpiration = () => {
    if (hasSpecificDates) {
      // Find the latest end_date from listtime array
      const listtimeArray = getListtimeArray()
      const latestDate = listtimeArray
        .map((item: any) => new Date(item.end_date))
        .reduce((latest: Date, current: Date) => (current > latest ? current : latest), new Date(0))
      return latestDate < new Date()
    }

    // Fallback to original logic for single date promotions
    if (promotionData?.listtime && !Array.isArray(promotionData.listtime)) {
      return new Date(promotionData.listtime.end_date) < new Date()
    }

    // Use expired field directly from API if available
    if (typeof promotionData.expired === 'boolean') {
      return promotionData.expired
    }

    return false
  }

  // Calculate expire date for constraints
  const getExpireDate = () => {
    if (hasSpecificDates) {
      // Find the latest end_date from listtime array
      const listtimeArray = getListtimeArray()
      const latestDate = listtimeArray
        .map((item: any) => item.end_date)
        .reduce((latest: string, current: string) => (current > latest ? current : latest), '')
      return latestDate || undefined
    }

    // Fallback to original logic
    if (promotionData?.listtime && !Array.isArray(promotionData.listtime)) {
      return (
        promotionData.products?.[0]?.expire_date || promotionData.listtime.end_date || undefined
      )
    }
    return promotionData.products?.[0]?.expire_date || undefined
  }
  const promotionConstraints = {
    branchIds: String(promotionData.PM_brachID || ''),
    minGuests: promotionData.mincust || 1,
    //__TYPE SAFETY FOR MAXCUST
    maxGuests:
      promotionData.maxcust && promotionData.maxcust >= (promotionData.mincust || 1)
        ? promotionData.maxcust
        : Math.max((promotionData.mincust || 1) * 2, 10),
    advancebooking: promotionData.advancebooking || 0,
    isBuyOneGetOne: promotionData.promotion_buyone === 1,
    expired: calculateExpiration(),
    expireDate: getExpireDate(),
    // New: Pass specific available dates and times from API
    availableDateTimes: hasSpecificDates ? getListtimeArray() : undefined,
    promotionUrl: promotionData.url || '',
    promotionId: promotionData.promotion_id || 0,
    cs_promotion_id: promotionData.promotion_id || 0
  }

  //__FALLBACK IMAGE
  const promotionImage = `${process.env.NEXT_PUBLIC_API_GATEWAY}/${promotionData.promotion_img || '/images/placeholder.jpg'}`

  //__PRODUCT DATA CHECK
  const hasProducts = promotionData.products && promotionData.products.length > 0
  const primaryProduct = hasProducts ? promotionData.products![0] : null

  return (
    <div className={`booking-online-container ${isVisible ? 'visible' : ''}`}>
      {/* <FirefliesEffect count={40} color='rgb(244, 115, 32)' size={0.4} speed={1.2} /> */}
      <UtmHandler />
      {promotionData && (
        <section className='hero-section'>
          <div className='hero-content'>
            {promotionData.promotion_lhead && (
              <h1 className='hero-title'>
                <span>{promotionData.promotion_lhead}</span>
              </h1>
            )}
            {promotionData.promotion_rhead && (
              <h2 className='hero-subtitle'>{promotionData.promotion_rhead}</h2>
            )}
            {promotionData.word_gotpromotion && (
              <p className='hero-tagline' dangerouslySetInnerHTML={{ __html: promotionData.word_gotpromotion }}></p>
            )}

            <div className='booking-button-wrapper'>
              <PromotionButtonBooking constraints={promotionConstraints} />
            </div>
          </div>

          {promotionImage && (
            <div className='hero-image-container'>
              <div className='image-frame'>
                <img
                  src={promotionImage}
                  alt={promotionData.promotion_lhead || 'Promotion'}
                  className='hero-image'
                />
              </div>
            </div>
          )}
        </section>
      )}

      <div className='content-container'>
        {promotionData.promotion_tdetail && (
          <section className='details-card primary-details'>
            <div className='card-header'>
              <div className='header-icon'>i</div>
              <h3 className='header-title'>Promotion Details</h3>
            </div>
            <div
              className='details-content'
              dangerouslySetInnerHTML={{ __html: promotionData.promotion_tdetail }}
            />
            <div className='booking-button-wrapper'>
              <PromotionButtonBooking constraints={promotionConstraints} />
            </div>
          </section>
        )}

        {promotionData.promotion_bdetail && (
          <section className='details-card secondary-details'>
            <div className='card-header'>
              <div className='header-icon'>✓</div>
              <h3 className='header-title'>Additional Information</h3>
            </div>
            <div
              className='details-content'
              dangerouslySetInnerHTML={{ __html: promotionData.promotion_bdetail }}
            />
          </section>
        )}
      </div>
    </div>
  )
}

export default Temlate_Booking_Online
